import BigNumber from 'bignumber.js'

export interface EulerSpendPowerLiquidity {
  collaterals: readonly string[]
  collateralValuesRawUSD: readonly BigNumber[]
  collateralValuesBorrowUSD: readonly BigNumber[]
  totalBorrowCapacityUSD: BigNumber
  totalDebtBorrowUSD: BigNumber
  /** Health factor for the sub-account (collateralValueLiquidation / liabilityValueLiquidation). Undefined when there's no debt. */
  healthFactor?: number
}

interface CalculateEulerSpendPowerUSDParams {
  collateralVaultAddress: string
  depositAmountUSD: BigNumber.Value
  liquidity?: EulerSpendPowerLiquidity
}

export function calculateEulerSpendPowerUSD({
  collateralVaultAddress,
  depositAmountUSD,
  liquidity,
}: CalculateEulerSpendPowerUSDParams): BigNumber {
  const depositUSD = new BigNumber(depositAmountUSD)
  if (!depositUSD.isFinite() || depositUSD.lte(0)) return new BigNumber(0)
  if (!liquidity || liquidity.totalDebtBorrowUSD.lte(0)) return depositUSD

  const collateralIndex = liquidity.collaterals.findIndex(
    (address) => address.toLowerCase() === collateralVaultAddress.toLowerCase(),
  )
  if (collateralIndex === -1) {
    // The vault isn't enabled as collateral on this sub-account, so its shares
    // don't contribute to the controller's health check. The `euler-withdraw`
    // backend (see `euler_withdraw_solver.go`) calls `vault.withdraw()` on the
    // sub-account directly - withdrawing pure savings is unconstrained by the
    // enabled collateral / debt, so spend power equals the full deposit.
    return depositUSD
  }

  const rawCollateralUSD = liquidity.collateralValuesRawUSD[collateralIndex] ?? depositUSD
  const borrowWeightedCollateralUSD = liquidity.collateralValuesBorrowUSD[collateralIndex] ?? new BigNumber(0)
  const remainingBorrowCapacityUSD = liquidity.totalBorrowCapacityUSD.minus(liquidity.totalDebtBorrowUSD)

  if (rawCollateralUSD.lte(0)) return new BigNumber(0)
  if (borrowWeightedCollateralUSD.lte(0)) return BigNumber.min(rawCollateralUSD, depositUSD)
  if (remainingBorrowCapacityUSD.lte(0)) return new BigNumber(0)

  const collateralBorrowLtv = borrowWeightedCollateralUSD.div(rawCollateralUSD)
  if (collateralBorrowLtv.lte(0)) return BigNumber.min(rawCollateralUSD, depositUSD)

  const withdrawableCollateralUSD = remainingBorrowCapacityUSD.div(collateralBorrowLtv)
  return BigNumber.max(BigNumber.min(withdrawableCollateralUSD, rawCollateralUSD, depositUSD), 0)
}
